import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '../../services/api';
import type { TeleconsultStaffCancelPreview } from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  VISIT_CANCEL_REASON_OTHER,
  isVisitCancelReasonValid,
} from '../staff-management/cancelReasons';
import { VisitCancelReasonPicker } from '../staff-management/VisitCancelReasonPicker';

type Props = {
  visitUlid: string;
  patientName: string;
  onClose: () => void;
  onCancelled: () => void;
};

function formatSgd(amount: number): string {
  return `SGD ${amount.toFixed(2)}`;
}

const DEFAULT_ADMIN_FEE = '2.00';

function parseFee(raw: string): number | null {
  const text = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const value = Math.round(Number(text) * 100) / 100;
  if (!Number.isFinite(value) || value < 0) return null;
  return value;
}

export function StaffQueueCancelDialog({ visitUlid, patientName, onClose, onCancelled }: Props) {
  const [preview, setPreview] = useState<TeleconsultStaffCancelPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reasonCode, setReasonCode] = useState('');
  const [reasonOther, setReasonOther] = useState('');
  const [adminFeeRaw, setAdminFeeRaw] = useState(DEFAULT_ADMIN_FEE);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  useEffect(() => {
    let cancelled = false;
    void apiService
      .getTeleconsultStaffCancelPreview(visitUlid)
      .then((dto) => {
        if (cancelled) return;
        setPreview(dto);
        if (dto.hasDeposit) {
          const cap = Math.min(2, dto.remainingSgd);
          setAdminFeeRaw(cap.toFixed(2));
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(getApiErrorMessage(err, 'Could not load the cancellation.'));
      });
    return () => {
      cancelled = true;
    };
  }, [visitUlid]);

  const reasonReady = isVisitCancelReasonValid(reasonCode, reasonOther);
  const remaining = preview?.remainingSgd ?? 0;
  const fee = parseFee(adminFeeRaw);
  const refund =
    preview?.hasDeposit && fee != null && fee <= remaining + 0.001
      ? Math.round((remaining - fee) * 100) / 100
      : null;

  const feeReady = !preview?.hasDeposit || (fee != null && fee <= remaining + 0.001 && refund != null && refund >= 0);
  const canSubmit = Boolean(preview?.canCancel) && reasonReady && feeReady && !busy;

  const reasonHint = useMemo(() => {
    if (reasonCode === VISIT_CANCEL_REASON_OTHER && !reasonReady) {
      return 'Describe the reason (at least 3 characters).';
    }
    return null;
  }, [reasonCode, reasonReady]);

  const submit = async () => {
    if (!canSubmit || !preview) return;
    setBusy(true);
    setSubmitError(null);
    try {
      await apiService.cancelTeleconsultFromStaffQueue(visitUlid, {
        reasonCode,
        reasonOther: reasonCode === VISIT_CANCEL_REASON_OTHER ? reasonOther.trim() : undefined,
        adminFeeSgd: preview.hasDeposit ? fee ?? undefined : undefined,
      });
      onCancelled();
    } catch (err: unknown) {
      setSubmitError(getApiErrorMessage(err, 'Could not cancel this visit.'));
      setBusy(false);
    }
  };

  return createPortal(
    <div className="staff-queue-cancel-backdrop" role="presentation" onClick={() => { if (!busy) onClose(); }}>
      <div
        className="staff-queue-cancel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="staff-queue-cancel-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="staff-queue-cancel-title" className="staff-queue-cancel__title">Cancel visit</h2>
        <p className="staff-queue-cancel__lead">{patientName}</p>

        {loadError ? <p className="staff-queue-cancel__error">{loadError}</p> : null}
        {!preview && !loadError ? <p className="staff-queue-cancel__lead">Loading…</p> : null}
        {preview && !preview.canCancel ? (
          <p className="staff-queue-cancel__error">{preview.blockedReason || 'This visit cannot be cancelled.'}</p>
        ) : null}

        {preview?.canCancel ? (
          <>
            <p id="staff-queue-cancel-reason-label" className="staff-queue-cancel__label">
              Cancellation reason
            </p>
            <VisitCancelReasonPicker
              name="staff-queue-cancel-reason"
              labelledBy="staff-queue-cancel-reason-label"
              value={reasonCode}
              disabled={busy}
              onChange={setReasonCode}
            />
            {reasonCode === VISIT_CANCEL_REASON_OTHER ? (
              <textarea
                className="staff-queue-cancel__input staff-queue-cancel__other"
                value={reasonOther}
                disabled={busy}
                maxLength={500}
                placeholder="What happened?"
                onChange={(e) => setReasonOther(e.target.value)}
              />
            ) : null}
            {reasonHint ? <p className="staff-queue-cancel__hint">{reasonHint}</p> : null}

            {reasonReady && preview.hasDeposit ? (
              <div className="staff-queue-cancel__refund">
                <p>
                  Deposit paid {formatSgd(preview.depositPaidSgd)}
                  {preview.alreadyRefundedSgd > 0 ? ` · already refunded ${formatSgd(preview.alreadyRefundedSgd)}` : ''}
                  {' · remaining '}
                  <strong>{formatSgd(remaining)}</strong>
                </p>
                <p className="staff-queue-cancel__label">Admin fee to keep (GST included). Default $2.00. Edit the amount, or choose a full refund.</p>
                <div className="staff-queue-cancel__choices">
                  {remaining >= 2 ? (
                    <button
                      type="button"
                      className={fee === 2 ? 'is-selected' : undefined}
                      disabled={busy}
                      onClick={() => setAdminFeeRaw(DEFAULT_ADMIN_FEE)}
                    >
                      Keep $2.00
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className={fee === 0 ? 'is-selected' : undefined}
                    disabled={busy}
                    onClick={() => setAdminFeeRaw('0.00')}
                  >
                    Full refund
                  </button>
                </div>
                <input
                  className="staff-queue-cancel__input"
                  inputMode="decimal"
                  aria-label="Admin fee"
                  value={adminFeeRaw}
                  disabled={busy}
                  onChange={(e) => setAdminFeeRaw(e.target.value)}
                />
                {refund != null ? (
                  <p className="staff-queue-cancel__preview">
                    Clinic keeps {formatSgd(fee ?? 0)}. Patient is refunded {formatSgd(Math.max(0, refund))}.
                  </p>
                ) : (
                  <p className="staff-queue-cancel__error">Enter an admin fee from 0.00 up to {formatSgd(remaining)}.</p>
                )}
              </div>
            ) : null}
          </>
        ) : null}

        {submitError ? <p className="staff-queue-cancel__error">{submitError}</p> : null}

        <div className="staff-queue-cancel__actions">
          <button type="button" className="staff-tele-btn staff-tele-btn--openchat" disabled={busy} onClick={onClose}>
            Close
          </button>
          {preview?.canCancel ? (
            <button
              type="button"
              className="staff-tele-btn staff-tele-btn--cancel"
              disabled={!canSubmit}
              onClick={() => void submit()}
            >
              {busy
                ? 'Working…'
                : preview.hasDeposit && (refund ?? 0) >= 0.01
                  ? 'Cancel and refund'
                  : 'Cancel visit'}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
